/**
 * 「休みにする」の時間指定（log_web_issues#338 C9）で使う窓の組み立てと検査。
 *
 * 画面（`LongHaulRestPopover`）は DateTimePicker が返す `Date` を持ち、送るときに
 * ここで ISO8601 へ直す。
 */

/** 時間指定の既定の開始時刻。サンプル（route-planner MultiDayBoard.tsx:198-205）と同じ */
const DEFAULT_START_HOUR = 15;
/** 時間指定の既定の終了時刻。翌日の朝 */
const DEFAULT_END_HOUR = 8;

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * 押したセルの日の 15:00 →（その）翌日の 08:00。読めない日付では null。
 *
 * Why not `new Date(date)`: 'YYYY-MM-DD' は UTC として解釈されるので、西側の
 * タイムゾーンでは前日になる（buildDropTargets.ts / spanDays.ts と同じ数値分解に揃える）。
 */
export const defaultRestWindow = (date: string): { start: Date; end: Date } | null => {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!matched) return null;
  const [year, month, day] = [Number(matched[1]), Number(matched[2]) - 1, Number(matched[3])];
  const start = new Date(year, month, day, DEFAULT_START_HOUR, 0);
  if (Number.isNaN(start.getTime())) return null;
  // 日付の繰り上がりは Date に任せる（月末・年末をここで場合分けしない）
  const end = new Date(year, month, day + 1, DEFAULT_END_HOUR, 0);
  return { start, end };
};

/**
 * 画面で選んだ壁時計の値に +09:00 を付けた ISO8601。秒は常に 00。
 *
 * Why not ブラウザのローカルオフセットを付ける: 盤面の日付は JST 固定で切っている
 * （`businessDayOf`）。ブラウザが JST 以外だと、選んだ日と実際に休みになる日がずれる。
 * Why not オフセットを付けない: API は付いていなければアプリのタイムゾーンで読むが
 * （TruckShifts::RestWindow）、送った値がどの時刻を指すのかがサーバー設定次第になる。
 */
export const toJstIso = (date: Date): string => {
  const ymd = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return `${ymd}T${pad(date.getHours())}:${pad(date.getMinutes())}:00+09:00`;
};

/**
 * 送る前の検査。通るなら null、止めるなら画面に出す理由を返す。
 *
 * API も `end_at <= start_at` を 422 で弾くが、往復させると理由が出るまでに間が空く。
 * 窓の長さに上限は無いので、複数日にわたる休みはここでも通す。
 */
export const restWindowError = (start: Date | null, end: Date | null): string | null => {
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return '開始と終了の日時を選んでください';
  }
  if (end.getTime() <= start.getTime()) return '終了は開始より後の日時を選んでください';
  return null;
};
